// Simple New Relic integration that works with React Router SSR
import type { BrowserAgent } from "@newrelic/browser-agent/loaders/browser-agent";

const isBrowser = typeof window !== "undefined";

// New Relic configuration
const getNewRelicConfig = () => {
    if (!isBrowser) return null;

    return {
        init: {
            distributed_tracing: { enabled: false },
            privacy: { cookies_enabled: true },
            ajax: { enabled: true },
            jserrors: { enabled: true },
            logging: { enabled: true, level: "warn" },
            metrics: { enabled: true },
            page_action: { enabled: false },
            page_view_event: { enabled: true },
            page_view_timing: { enabled: true },
            session_trace: { enabled: false },
            spa: { enabled: true },
        },
        info: {
            beacon: "bam.nr-data.net",
            errorBeacon: "bam.nr-data.net",
            licenseKey: import.meta.env.VITE_NEWRELIC_LICENSE_KEY || "",
            applicationID: import.meta.env.VITE_NEWRELIC_APPLICATION_ID || "",
            sa: 1,
        },
        loader_config: {
            accountID: import.meta.env.VITE_NEWRELIC_ACCOUNT_ID || "",
            trustKey: import.meta.env.VITE_NEWRELIC_TRUST_KEY || "",
            agentID:
                import.meta.env.VITE_NEWRELIC_AGENT_ID ||
                import.meta.env.VITE_NEWRELIC_APPLICATION_ID ||
                "",
            licenseKey: import.meta.env.VITE_NEWRELIC_LICENSE_KEY || "",
            applicationID: import.meta.env.VITE_NEWRELIC_APPLICATION_ID || "",
        },
    };
};

let agent: BrowserAgent | null = null;

// Initialize New Relic Browser Agent
export const initializeNewRelic = async () => {
    if (!isBrowser || agent) return agent;

    const config = getNewRelicConfig();
    if (!config || !config.info.licenseKey) {
        return null;
    }

    try {
        // Dynamic import to avoid SSR issues
        const { BrowserAgent } = await import(
            "@newrelic/browser-agent/loaders/browser-agent"
        );

        agent = new BrowserAgent(config);

        // Set custom attributes AFTER creating the agent but BEFORE starting it
        const environment = import.meta.env.VITE_ENVIRONMENT || "development";
        const releaseVersion =
            import.meta.env.VITE_RELEASE_VERSION || "unknown";

        agent.setCustomAttribute("environment", environment);

        agent.start();

        // used to link errors to the sourcemaps in New Relic
        agent.addRelease("scpportal", `${environment}-${releaseVersion}`);

        return agent;
    } catch (error) {
        return null;
    }
};

// Setup basic global monitoring - only uncaught errors
export const setupGlobalMonitoring = () => {
    if (!isBrowser || !agent) return;

    // JavaScript errors (uncaught exceptions)
    window.addEventListener("error", event => {
        if (agent) {
            agent.noticeError(event.error || new Error(event.message), {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                type: "javascript_error",
            });
        }
    });
};

// Custom error reporting function
export const reportError = (error: Error, context?: any) => {
    if (isBrowser && agent) {
        agent.noticeError(error, context);
    }
};

export default initializeNewRelic;
